import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import PageWrapper from '../../components/PageWrapper';
import AnimatedSection from '../../components/AnimatedSection';
import {
  FiBarChart,
  FiFileText,
  FiClipboard,
  FiCheckCircle,
  FiTrendingUp,
  FiShield,
  FiTarget
} from 'react-icons/fi';
import RmbIcon from '../../components/RmbIcon';
import './Services.css';

const Services = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('investment');

  // 容器动画 variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) setActiveTab(hash);
  }, [location]);

  const services = {
    investment: {
      title: '投资估算咨询、编制及审核服务',
      icon: <FiBarChart />,
      subtitle: '为项目投资决策提供科学依据',
      description: '在项目建议书和可行性研究阶段，根据项目建设规模、标准、建设条件等，采用指标估算法、类比法等方法，编制投资估算文件，为投资决策提供依据。同时提供投资估算审核服务，确保估算的准确性和合理性。',
      scope: [
        '建设项目建议书阶段投资估算编制',
        '可行性研究阶段投资估算编制',
        '投资估算审核与复核',
        '投资回报分析与敏感性测试',
        '投资构成分析与优化建议',
        '不同方案投资对比分析'
      ],
      deliverables: [
        '投资估算书（含总投资、建安工程费、设备购置费等）',
        '投资估算审核报告',
        '投资回报测算模型',
        '经济评价分析报告'
      ]
    },
    design: {
      title: '设计概算咨询、编制及审核服务',
      icon: <FiTarget />,
      subtitle: '设计阶段投资控制的关键环节',
      description: '在初步设计阶段，根据设计图纸、技术方案、施工组织设计等，按照国家和地方定额标准，编制设计概算。提供限额设计咨询，确保设计方案在投资限额内优化。同时提供概算审核服务，把好设计阶段投资控制关。',
      scope: [
        '初步设计概算编制',
        '设计概算审核与复核',
        '限额设计全过程咨询',
        '设计方案经济比选',
        '概算与估算对比分析',
        '超概预警与优化建议'
      ],
      deliverables: [
        '设计概算书（含单项工程概算、单位工程概算、综合概算）',
        '概算审核报告与审核意见',
        '限额设计控制指标',
        '设计优化方案建议'
      ]
    },
    budget: {
      title: '工程预算咨询、编制及审核服务',
      icon: <FiFileText />,
      subtitle: '施工图阶段精准造价控制',
      description: '在施工图设计完成后，根据施工图纸、施工组织设计、现行定额及取费标准，编制施工图预算。为招标控制价编制、投标报价提供依据。提供预算审核服务，确保工程量计算准确、定额套用合理、材料价格真实。',
      scope: [
        '施工图预算编制',
        '施工图预算审核与复核',
        '工程量清单编制',
        '招标控制价编制与审核',
        '综合单价分析',
        '预算与概算对比分析'
      ],
      deliverables: [
        '施工图预算书（含工程量清单、综合单价分析表）',
        '预算审核报告',
        '招标控制价文件',
        '工程量复核清单'
      ]
    },
    settlement: {
      title: '工程结算咨询、编制及审核服务',
      icon: <FiCheckCircle />,
      subtitle: '竣工阶段成本核算与审核',
      description: '在工程竣工后，根据竣工图纸、合同条款、变更签证、现场签证、材料价格等，编制竣工结算。提供结算审核服务，采用AI智能审核+人工专家复核模式，精准识别多算、漏算、高套定额等问题，出具审减明细与整改意见。',
      scope: [
        '竣工结算编制',
        '竣工结算审核（AI+人工双审）',
        '变更签证核定与审核',
        '索赔事项处理',
        '合同价款调整审核',
        '结算争议调解与鉴定'
      ],
      deliverables: [
        '竣工结算书',
        '结算审核报告（含审减明细、问题清单）',
        '变更签证汇总表',
        '结算争议处理意见'
      ]
    },
    other: {
      title: '工程其他费咨询、编制及审核服务',
      icon: <RmbIcon size={40} />,
      subtitle: '全口径费用测算与管控',
      description: '针对建设单位管理费、勘察设计费、监理费、招标代理费、前期咨询费、临时设施费、专项评估费等工程其他费用，提供测算编制与审核服务，形成全口径投资控制清单，避免费用遗漏或超标。',
      scope: [
        '建设单位管理费测算与审核',
        '前期工作咨询费（可研、勘察、设计等）',
        '工程建设其他费用（监理、招标、检测等）',
        '预备费与价差预备费测算',
        '专项评估费用（环评、稳评、地灾评估等）',
        '临时设施费与场地准备费'
      ],
      deliverables: [
        '工程其他费用清单',
        '其他费用审核报告',
        '费用构成分析表',
        '优化建议与节约方案'
      ]
    },
    final: {
      title: '项目决算咨询、编制及审核服务',
      icon: <FiTrendingUp />,
      subtitle: '竣工财务决算与后评价',
      description: '在项目竣工验收后，根据竣工结算、财务资料、合同文件等，编制竣工财务决算，反映项目实际投资情况。提供决算审核服务，分析投资偏差原因，形成后评价报告，为同类项目积累经验数据。',
      scope: [
        '竣工财务决算编制',
        '竣工财务决算审核',
        '投资完成情况分析',
        '投资偏差原因分析',
        '项目后评价',
        '造价指标库建立'
      ],
      deliverables: [
        '竣工财务决算书',
        '决算审核报告',
        '投资偏差分析报告',
        '项目后评价报告',
        '造价指标数据'
      ]
    },
    tendering: {
      title: '招投标代理服务、政府采购代理服务',
      icon: <FiClipboard />,
      subtitle: '全流程招标组织与合规管理',
      description: '提供工程、货物、服务类项目的招标代理和政府采购代理服务。覆盖招标文件编制、资格预审、招标公告发布、投标文件接收、开标评标组织、中标公示、合同签订等全流程，确保招投标活动合法合规、公开透明。',
      scope: [
        '招标文件编制（技术标准、商务条款、评标办法）',
        '资格预审文件编制与审查',
        '招标公告发布与信息管理',
        '投标文件接收与密封管理',
        '开标评标会议组织',
        '评标报告编制与中标公示',
        '政府采购代理（公开招标、竞争性谈判、询价、单一来源）',
        '电子化招投标平台服务'
      ],
      deliverables: [
        '招标文件（含工程量清单、技术规范、合同范本）',
        '资格预审文件',
        '评标报告',
        '中标通知书',
        '招标过程全程留痕资料'
      ]
    },
    procurement: {
      title: '采购业务代理服务',
      icon: <FiShield />,
      subtitle: '材料设备采购专业支持',
      description: '针对大宗材料、机电设备、专业分包等采购需求，提供市场询价、供应商考察、商务谈判、合同起草、履约管理等全流程服务。通过集采降本、质量把控、履约监督，帮助业主优化采购成本与供应链管理。',
      scope: [
        '采购需求分析与策略制定',
        '市场询价与供应商调研',
        '采购文件编制（技术要求、商务条款）',
        '商务谈判与合同起草',
        '履约过程监督与验收',
        '采购成本分析与优化'
      ],
      deliverables: [
        '采购策略方案',
        '市场询价报告',
        '采购合同文本',
        '履约监督报告',
        '采购成本分析表'
      ]
    }
  };

  const current = services[activeTab];

  return (
    <PageWrapper className="services-page">
      <AnimatedSection className="services-hero">
        <div className="container">
          <div className="hero-content">
            <motion.div variants={itemVariants}>
              <span className="hero-eyebrow">SIDAO SERVICE SUITE</span>
              <h1>识道造价事务所 · 八大专业服务体系</h1>
              <p>
                涵盖投资估算、设计概算、工程预算、竣工结算、项目决算、其他费用等造价全链条服务，
                以及招投标代理、采购代理等专业服务，为项目全生命周期提供造价治理支撑。
              </p>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="services-tabs">
        <div className="container">
          <motion.div
            className="tab-list"
            variants={itemVariants}
          >
            {Object.keys(services).map((key) => (
              <button
                key={key}
                className={`tab-item ${activeTab === key ? 'active' : ''}`}
                onClick={() => setActiveTab(key)}
                title={services[key].title}
              >
                <span className="tab-icon">{services[key].icon}</span>
                <span className="tab-text">{services[key].title}</span>
              </button>
            ))}
          </motion.div>

          <motion.div
            className="service-detail-content"
            variants={itemVariants}
          >
            <motion.div
              className="service-header"
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="service-icon-large">{current.icon}</div>
              <div>
                <h2>{current.title}</h2>
                <p className="service-subtitle">{current.subtitle}</p>
                <p className="service-description">{current.description}</p>
              </div>
            </motion.div>

            <motion.div
              className="service-scope"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
            >
              <h3>服务范围</h3>
              <div className="scope-grid">
                {current.scope.map((item, index) => (
                  <motion.div
                    key={index}
                    className="scope-item"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                  >
                    <FiCheckCircle />
                    <span>{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="service-deliverables"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <h3>交付成果</h3>
              <div className="deliverables-grid">
                {current.deliverables.map((item, index) => (
                  <motion.div
                    key={index}
                    className="deliverable-card"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + index * 0.05 }}
                  >
                    <div className="deliverable-number">0{index + 1}</div>
                    <span>{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="service-cta"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div>
                <h3>需要{current.title.split('、')[0]}？</h3>
                <p>提交需求后，资深造价工程师将在 2 小时内与您联系，提供专业咨询与解决方案。</p>
              </div>
              <div className="cta-actions">
                <a href="/contact" className="btn btn-primary">立即咨询</a>
                <a href="/platform" className="btn btn-secondary">了解数智平台</a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </AnimatedSection>
    </PageWrapper>
  );
};

export default Services;